import { HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import { MailQuestion, MailCheck } from "lucide-react";
import { memo, type MouseEvent as ReactMouseEvent } from "react";
import { queryChipState, type QueryAccount } from "../reviewLogic";

/**
 * QueryChip — reviewer-side query status on an account row (PLO-4816).
 *   - "Queried": the reviewer sent this account to the IAM team; still awaiting
 *     a response. Neutral styling.
 *   - "Response received": the IAM team answered and the account is back in
 *     requires_review — the reviewer needs to re-decide. Emphasis styling; when
 *     `onClick` is given it's clickable (opens the account drawer).
 * Renders nothing when there's no query in play.
 *
 * `show` filters which state this instance renders, so the "Response received"
 * chip can sit inline next to the account name while the "Queried" chip stays in
 * the meta stack (PLO-4803).
 *
 * The two visual chips are also exported (`QueriedChipVisual` /
 * `AnsweredChipVisual`) so review-scope general queries (PLO-4803) can reuse the
 * exact styling with their own labels, without an account.
 */

/** Neutral "awaiting a response" chip. Presentational; label + tooltip vary. */
export const QueriedChipVisual = ({
  label = "Queried",
  tooltip = "Sent to the IAM team. Awaiting their response.",
}: {
  label?: string;
  tooltip?: string;
}) => (
  <Tooltip label={tooltip} placement="top" hasArrow openDelay={300}>
    <HStack
      spacing="4px"
      px="6px"
      py="2px"
      bg="#F1F4F6"
      borderRadius="6px"
      border="1px solid"
      borderColor="#D9E0E3"
      flexShrink={0}
    >
      <Icon as={MailQuestion} boxSize="12px" color="#647884" />
      <Text
        fontSize="12px"
        fontWeight={600}
        color="#647884"
        lineHeight="1.2"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </HStack>
  </Tooltip>
);

/** Emphasis "response received" chip. Clickable when `onClick` is given. */
export const AnsweredChipVisual = ({
  label = "Response received",
  tooltip,
  onClick,
}: {
  label?: string;
  tooltip?: string;
  onClick?: () => void;
}) => {
  const clickable = !!onClick;
  const handleClick = (e: ReactMouseEvent) => {
    if (!onClick) return;
    e.stopPropagation();
    onClick();
  };
  return (
    <Tooltip
      label={
        tooltip ??
        (clickable
          ? "The IAM team responded. Open the account to review and re-decide."
          : "The IAM team responded to your query. Review and re-decide.")
      }
      placement="top"
      hasArrow
      openDelay={300}
    >
      <HStack
        spacing="4px"
        px="6px"
        py="2px"
        bg="p.100"
        borderRadius="6px"
        border="1px solid"
        borderColor="p.200"
        flexShrink={0}
        cursor={clickable ? "pointer" : "default"}
        onClick={clickable ? handleClick : undefined}
        _hover={clickable ? { bg: "p.200" } : undefined}
        transition="background-color 120ms"
      >
        <Icon as={MailCheck} boxSize="12px" color="p.700" />
        <Text
          fontSize="12px"
          fontWeight={600}
          color="p.700"
          lineHeight="1.2"
          whiteSpace="nowrap"
        >
          {label}
        </Text>
      </HStack>
    </Tooltip>
  );
};

export interface QueryChipProps {
  account: QueryAccount;
  /** which chip state(s) this instance renders (default: both). */
  show?: "all" | "queried" | "answered";
  /** makes the answered chip clickable (e.g. open the account drawer). */
  onClick?: () => void;
}

const QueryChip = memo(({ account, show = "all", onClick }: QueryChipProps) => {
  const state = queryChipState(account);
  if (state === null) return null;
  if (show === "queried" && state !== "queried") return null;
  if (show === "answered" && state !== "answered") return null;

  if (state === "answered") {
    return <AnsweredChipVisual onClick={onClick} />;
  }

  return <QueriedChipVisual />;
});

QueryChip.displayName = "QueryChip";

export default QueryChip;
